import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Collapse, {Panel} from 'rc-collapse';
import moment from 'moment';
import {FormProvider, useFieldArray, useForm} from 'react-hook-form';
import {joiResolver} from '@hookform/resolvers/joi';
import _debounce from 'lodash/debounce';
import Big from 'big.js';

import {
    addProlongCalculate,
    clearModalError,
    clearProlongCalculate,
    getClientContract,
    getClientContracts,
    getProlongCalculate,
    resetSave,
} from '../../../../redux/actions/contractAction';
import {HDatePicker, HInput} from '../../../../validation/HForm';
import prolongSchema from './prolongModalSchema';
import {ActionButton, ConfirmModal} from '../../../common';
import {
    calculationAccess,
    formattingEndDealDate,
    getExpectedMessage,
    getUserFromLocalStorage,
    permissionsAccess,
    returnEndStr,
    returnStatus,
    translateErrorMessage,
} from '../../../../helpers/common';
import {messages} from '../../../../helpers/messages';
import {Message} from '../..';
import Loader from './Loader/Loader';
import {
    basisForCloseAndProlongDeal,
    listRoles,
    nonAvailableFunction,
    paymentErrors,
    viewReports
} from '../../../../helpers/data-constants';

import './prolongModalLayout.scss';
import NonCash from "../../NonCash/NonCash";
import BasicAlert from "../../Alert/Alert";
import PaymentTypeSelector from '../../PaymentSelector/PaymentTypeSelector';
import {nonCashValidate} from "../../../../validation/scheme/nonCash";
import PenaltySelector from './PenaltySelector/PenaltySelector';
import { useProlongCalculations } from './calculators/useProlongCalculator';

const errorMaxDaysForExtension = 'Достигнуто максимальное количество оплачиваемых дней продления';
const errorDelayDaysLimitReached = 'Достигнуто максимальное количество оплачиваемых дней неустойки';
const errorProlongMaxDaysAndDelayDaysLimitReached = 'Достигнуто максимальное количество оплачиваемых дней по неустойке и продлению';
const accessError = 'Ошибка! Недостаточно прав доступа.';

const rolesForWaiverOfRedemptionDeals = [listRoles.ADMIN, listRoles.DIRECTOR];

const ProlongModalLayout = ({
                                paramReq,
                                handleCloseModal,
                                clientId,
                                paramReqList = null,
                                cashBoxId,
                                page = false,
                                status
                            }) => {
    const {
        modalError,
        prolongCalculate,
        isModalLoading,
        saveProlongCalculate,
        clientContract: {waiverOfRedemption, lastPaidDate, startDate, endDate, days},
    } = useSelector(({contract}) => contract,);
    const [state, setState] = useState({
        deliveryValue: 0, deliveryShow: false, introduced: 0, isModal: false,
    });
    const [isProlongDeal, setIsProlongDeal] = useState(false);
    const [error, setError] = useState('');
    const [disableButton, setDisableButton] = useState(false);
    const [basicAlertModal, setBasicAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState(paymentErrors.NON_CASH_ERROR);
    const [change, setChange] = useState(0);
    const [paymentInputs, setPaymentInputs] = useState([])
    const [selectedPenalties, setSelectedPenalties] = useState({});
    const [totalPenaltyAmount, setTotalPenaltyAmount] = useState(0);
    const {accessFeatures} = useSelector(({user}) => user);
    const dispatch = useDispatch();
    const dataParamReq = useRef();
    const currentDate = moment();
    const calculatedMinDate = paramReq.reduce((acc, i) => (moment(i.endDate).valueOf() > moment(acc).valueOf() ? acc : i.endDate), paramReq[0].endDate,)
    const minDate = formattingEndDealDate(calculatedMinDate);
    const [isCalculated, setIsCalculated] = useState(false);

    const isExtendWithDaysLimit = accessFeatures["deal.extension.days.limit"];
    const isExtensionThreeImplementaition = accessFeatures["extension.implementation.type.three"]

    const form = useForm({
        mode: 'all',
        defaultValues: {
            deals: [{
                dealId: '', extensionDate: '', sum: '',
            },], deposit: {
                cash: 0, nonCash: 0, bankTransaction: 0
            }
        },
        resolver: joiResolver(prolongSchema(moment(prolongCalculate && prolongCalculate[0]?.firstEndDate).isSameOrBefore(moment(currentDate)) ? moment().format('YYYY-MM-DD') : prolongCalculate[0]?.firstEndDate, isExtendWithDaysLimit)),
    });

    const cashValue = form.watch('deposit.cash') || 0;
    const nonCashValue = form.watch('deposit.nonCash') || 0;
    const nonCashAccountValue = form.watch('deposit.nonCashAccount') || 0;
    const bankTransactionValue = form.watch('deposit.bankTransaction') || 0;
    const total = +cashValue + +nonCashValue + +nonCashAccountValue + +bankTransactionValue;
    const cashValueMinusChange = +cashValue - change
    const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type']

    const {append, remove, ...fieldArray} = useFieldArray({
        control: form.control, name: 'deals', keyName: 'dealId',
    });

    const dateCalculatorConfig = {
        paramReq,
        prolongCalculate,
        status,
        days,
        lastPaidDate,
        startDate,
        endDate,
        minDate,
        isExtendWithDaysLimit,
        isExtensionThreeImplementaition,
    };

    const { prolongCalculator, isNewGlobalProlong } = useProlongCalculations(dateCalculatorConfig);
    
    const notEmpty = prolongCalculate && prolongCalculate.length !== 0;
    const totalSum = notEmpty && prolongCalculate
        .map((i, index) => {
            const baseAmount = new Big(i?.baseAmount || 0).toFixed(2);
            const delayAmount = isNewGlobalProlong(index) ? new Big(0) : new Big(i?.delayAmount || 0).toFixed(2);
            const extensionAmount = new Big(i?.extensionAmount || 0).toFixed(2);
            const prolongAmount = new Big(i?.prolongAmount || 0).toFixed(2);

            return new Big(baseAmount)
                .plus(delayAmount)
                .plus(extensionAmount)
                .plus(prolongAmount);
    })
    .reduce((p, c) => p.plus(c), new Big(0))
    .toFixed(2);
    const totalSumExtensionAmount = notEmpty && prolongCalculate
    .map((i) => new Big(i?.baseAmount ? i.baseAmount.toFixed(2) : 0).plus(i?.extensionAmount ? i.extensionAmount.toFixed(2) : 0),)
    .reduce((p, c) => new Big(p).plus(c), 0)
    .toFixed(2);
    const totalSumPercent = notEmpty && prolongCalculate
    .map((i) => new Big(i?.delayAmount ? i.delayAmount.toFixed(2) : 0))
    .reduce((p, c) => new Big(p).plus(c), 0)
    .toFixed(2);
    /* todo transfer the calculation to the back */
    const userRoles = getUserFromLocalStorage().roles;
     
    const defaultValueMainHDatePicker = moment(minDate).isSameOrAfter(moment(paramReq[0].endDate)) ? minDate : moment().format('YYYY-MM-DD');
 
    useEffect(() => {
        dataParamReq.current = paramReq.map((i, index) => ({
            dealId: i.id, extensionDate: prolongCalculator[index]?.baseExtensionDateDefault(i), sum: '',
        }));
        dispatch(getProlongCalculate(dataParamReq.current));

        return () => {
            dispatch(clearProlongCalculate());
            dispatch(clearModalError());
        };
    }, [dispatch, paramReq]);

    const accessWaiverOfRedemptionDeals = calculationAccess(rolesForWaiverOfRedemptionDeals);

    useEffect(() => {
        if (!accessWaiverOfRedemptionDeals && waiverOfRedemption) {
            setDisableButton(true)
            return setError(accessError);
        }
        if (userRoles.includes(listRoles.GOODS_SPECIALIST) && !userRoles.includes(listRoles.ADMIN) && paramReq.find((i) => i.cashBoxId !== cashBoxId)) {
            setDisableButton(true)
            return setError('Договор(а) из другого подразделения');
        }
    }, [accessWaiverOfRedemptionDeals, waiverOfRedemption, cashBoxId, paramReq, userRoles]);

    useEffect(() => {
        if (!isModalLoading && prolongCalculate?.length && dataParamReq.current) {
            const data = prolongCalculate.map((item, index) => {
                const deal = dataParamReq.current.find((i) => i.dealId === item.dealId);

                return {
                    extensionDate: item?.newEndDate && !isNewGlobalProlong(index) ? item.newEndDate : deal.extensionDate,
                    dealId: item.dealId,
                    sum: deal.sum,
                };
            });
            remove();
            append(data);
        }
    }, [isModalLoading, prolongCalculate, append, remove]);

    useEffect(() => {
        if (saveProlongCalculate && page) {
            dispatch(getClientContract(saveProlongCalculate[0].dealId));
            handleCloseModal('isProlong');
        }

    if (saveProlongCalculate && !page) {
      const data = {
        size: paramReqList.pageRange,
        page: paramReqList.currentPage,
        status: returnStatus(paramReqList.status),
        id: clientId,
        descending: true,
        sortBy: 'id'
      }
      dispatch(getClientContracts(data));

      handleCloseModal('isProlong');
    }

        return () => dispatch(resetSave());
    }, [saveProlongCalculate, clientId, dispatch, handleCloseModal, paramReq.length, paramReqList, page,]);

    const handleChange = () => {
        const formData = form.getValues();

        if (formData.deals && !Boolean(form.formState.errors.dateHeader)) {
            const data = formData.deals.map((i) => ({
                ...i, dealId: +i.dealId, extensionDate: formData.dateHeader,
            }));
        
            dispatch(getProlongCalculate(data));
            setState({introduced: 0, deliveryValue: 0, deliveryShow: false});
    
            dataParamReq.current = data;
        }
    };

    const debounceHandleChange = _debounce(handleChange, 1000);

    const setNotAvailableFunctionError = (formData) => {
        const updatedDeals = formData.deals.map(deal => ({
            ...deal,
            sum: ''
        })); 
        form.setValue('deals', updatedDeals);
        setBasicAlertModal(true);
        setAlertMessage(nonAvailableFunction)
    }

    const handleChangeItem = (targetField, index, dealId) => {
        const formData = form.getValues();
        if (!isNewGlobalProlong(index) && isExtendWithDaysLimit && targetField.value) {
            setNotAvailableFunctionError(formData)
            return
        } 
        if (formData.deals) {
            const data = formData.deals.map((i, idx) => {
                const updatedDeal = { ...i, dealId: +i.dealId, penaltiesNumbers: undefined };

                    if (idx === index) {
                        updatedDeal.extensionDate = targetField.value ? '' : i.extensionDate;
                        updatedDeal.sum = targetField.value ? i.sum : '';
                    }

                    return updatedDeal;
            });
            dispatch(getProlongCalculate(data));
            setState({introduced: 0, deliveryValue: 0, deliveryShow: false});
            setTotalPenaltyAmount(0);
            setSelectedPenalties(prev => {
                const { [dealId]: removed, ...rest } = prev;
                return rest;
            });
            if (formData.deals[index]?.sum) {
                setIsCalculated(true);
            } else {
                setIsCalculated(false);
            }

            dataParamReq.current = data;
        }
    };

    const debounceHandleChangeItem = _debounce((event, index, dealId) => handleChangeItem(event, index, dealId), 2000);

    const showDelivery = (e) => {
        e.persist();
        let deliveryValue = new Big(e.target.value ? e.target.value : 0).minus(totalSum);
        setState((prev) => ({
            ...prev, deliveryShow: true, deposit: {
                cash: +cashValue, nonCash: +nonCashValue, bankTransaction: +bankTransactionValue,
            }, deliveryValue: +deliveryValue.toFixed(2),
        }));
    };

    const handleInputChange = (e) => {
        e.persist();
        let deliveryValue = new Big(e.target.value ? e.target.value : 0).minus(totalSum);
        setState((prev) => ({
            ...prev, deliveryValue: +deliveryValue.toFixed(2),
        }));
    };

    const handleLocalOpenModal = () => setState((prev) => ({...prev, isModal: true}));

    const handleLocalCloseModal = () => setState((prev) => ({...prev, isModal: false}));

    const goBack = () => {
        handleLocalCloseModal();
        handleCloseModal('isProlong');
    };

    const onCloseModal = () => {
        handleLocalCloseModal();
        setIsProlongDeal(false);
    };

    const handlePenaltyAmount = (dealId, penaltyNumber, penaltyAmount, isSelected) => {
        setSelectedPenalties(prev => {
            const dealPenalties = prev[dealId] || [];
            const updatedPenalties = isSelected
                ? [...dealPenalties, penaltyNumber]
                : dealPenalties.filter(num => num !== penaltyNumber && num <= penaltyNumber);

            return {
                ...prev,
                [dealId]: isSelected ? [...new Set(updatedPenalties)] : updatedPenalties,
            };
        })

        setTotalPenaltyAmount(prev => {
            const updatedSelectedPenalties = {
                ...selectedPenalties,
                [dealId]: isSelected
                    ? [...new Set([...(selectedPenalties[dealId] || []), penaltyNumber])]
                    : (selectedPenalties[dealId] || []).filter(num => num < penaltyNumber)
            };

            let newTotalPenaltyAmount = 0;

            prolongCalculate.forEach(deal => {
                const dealId = deal.dealId;
                const selectedForDeal = updatedSelectedPenalties[dealId] || [];

                if (deal.penalties && selectedForDeal.length > 0) {
                    const dealPenaltySum = deal.penalties
                        .filter(penalty => selectedForDeal.includes(penalty.number))
                        .reduce((sum, penalty) => sum + penalty.penalty, 0);

                    newTotalPenaltyAmount += dealPenaltySum;
                }
            });

            return newTotalPenaltyAmount;
        });
    };

    const totalSumWithPenalties = useMemo(() => {
        const sum = parseFloat(totalSum) || 0;
        const penalty = parseFloat(totalPenaltyAmount) || 0;
        return new Big(sum).plus(penalty).toFixed(2);
      }, [totalSum, totalPenaltyAmount]);

    const handleSubmit = () => {

        const { error } = nonCashValidate.validate({
            cash: cashValue,
            nonCash: nonCashValue,
            nonCashAccount: nonCashAccountValue,
            bankTransaction: bankTransactionValue
        });

        const expectedTotal = isNonCashOperationCrediton ? (total - change).toFixed(2) : total.toFixed(2);
        const expectedMessage = getExpectedMessage(isNonCashOperationCrediton, paymentInputs)
        if (expectedTotal !== (totalSumWithPenalties ?? totalSum) || error) {
            setBasicAlertModal(true)
            setAlertMessage(expectedMessage)
            return;
        }

        handleLocalOpenModal();
        setIsProlongDeal(true);
    };

    const onProlongContract = () => {
        handleLocalCloseModal();
        setIsProlongDeal(false);

        const req = notEmpty && prolongCalculate.filter((i) => !i.message);

        if (req && req.length) {
            
            const data = req.map((i, index) => ({
                dealId: i.dealId, 
                extensionDate: i.newEndDate && !isNewGlobalProlong(index) ? i.newEndDate : getExtensionDate(i.dealId), 
                checksum: i.checksum,
                penaltiesNumbers: selectedPenalties[i.dealId] || undefined
            }));
            const deposit = {
                cash: +cashValueMinusChange.toFixed(2),
                nonCash: +nonCashValue,
                nonCashAccount: +nonCashAccountValue,
                bankTransaction: +bankTransactionValue,
            }
            const reqBody = data.map(({dealId}) => ({dealId, cashBoxId}));
            const filename = 'exp_' + moment().format('DD.MM.YYYY') + '.pdf';
            dispatch(addProlongCalculate(data, deposit, {
                reqBody, filename, viewReport: viewReports.SUPPLEMENTARY_AGREEMENT,
            }),);
        }
    };

    const showInfoMessage = (fieldPosition) => {
        const condition = {
            maxDaysReached: fieldPosition?.extensionMaxDays === 0 || fieldPosition?.isBaseDaysLimitReached,
            delayDayReached: fieldPosition?.isDelayDaysLimitReached
        }

        let messageText

        switch (true) {
            case condition.maxDaysReached && condition.delayDayReached:
                messageText = errorProlongMaxDaysAndDelayDaysLimitReached
                break
            case condition.maxDaysReached:
                messageText = errorMaxDaysForExtension
                break
            case condition.delayDayReached:
                messageText = errorDelayDaysLimitReached
                break
            default:
                return null
        }

        return <Message type="info" text={messageText}/>
    }

    const getExtensionDate = (dealId) => form.getValues().deals.find(deal => +deal.dealId === dealId).extensionDate
  
    const headerContent = (index) => {
        if (isNewGlobalProlong(index)) {
            return basisForCloseAndProlongDeal.NEW_BASE_EXPIRE;
        }
        
        return accessFeatures['cashboxNameTypeOne'] 
            ? basisForCloseAndProlongDeal.CREDITON.BASE_EXPIRE 
            : basisForCloseAndProlongDeal.VISEM.BASE_EXPIRE;      
    };

    const renderProlongErrorMessage = (error, idx) => {
        if (!error?.errorCode && !modalError) return null;
        const messageText = getErrorMessage(error, idx);     
        return (
            messageText && <Message key={idx} type="error" text={messageText} />
        );
    };

    const getErrorMessage = (error, idx) => {
        if (error?.errorCode === 16410) {
            return `${messages[16410]} ${returnEndStr(paramReq[idx]?.days ?? days)}`;
        }
        return Array.isArray(modalError) ? messages[error?.errorCode] : modalError;
    };

    const checkPaymentOrExtension = (date, index) => {
        const currentDate = moment();
        const isPaymentDue = moment(date).isSameOrBefore(currentDate);
        const shouldProlong = isNewGlobalProlong(index) && isExtensionThreeImplementaition;
        return isPaymentDue && shouldProlong ? 'оплата' : 'продление';
    };

    return (<>
            {(error) && <Message type="error" text={error}/>}
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                    {!isExtendWithDaysLimit ? <div className="modal-header">
                        <h2 className="input">Дата</h2>
                        <div className="prolong-error-message">
                            <HDatePicker
                                className="input"
                                name="dateHeader"
                                defaultValue={defaultValueMainHDatePicker}
                                onInput={debounceHandleChange}
                            />
                        </div>
                    </div> 
                    : <div className="modal-header">
                        <h2>Продление договора</h2>
                    </div>}
                <div className="modal-body">
                    {<>
                        {isModalLoading ? (<Loader pageRange={prolongCalculate.length || paramReq.length}/>) : (<>
                            {notEmpty && fieldArray.fields.map((i, idx) => {
                                const fieldPosition = prolongCalculate[idx];
                                console.log(i.dealId)
                                const notEmptyBaseAmount = fieldPosition.hasOwnProperty('baseAmount');
                                const notEmptyDelayAmount = fieldPosition.hasOwnProperty('delayAmount');
                                const notEmptyExtensionAmount = fieldPosition.hasOwnProperty('extensionAmount');
                                const baseAmount = notEmptyBaseAmount ? fieldPosition.baseAmount : 0;
                                const delayAmount = notEmptyDelayAmount ? fieldPosition.delayAmount.toFixed(2) : 0;
                                const basePercent = fieldPosition?.basePercent ? fieldPosition.basePercent : 0;
                                const firstBasePercent = fieldPosition?.firstBasePercent ? fieldPosition.firstBasePercent : basePercent;
                                const extensionAmount = notEmptyExtensionAmount ? fieldPosition.extensionAmount.toFixed(2) : 0;
                                const extensionDate = dataParamReq.current[idx].extensionDate;
                                const dateFromSum = fieldPosition?.dateFromSum;
                                const newEndDate = fieldPosition?.newEndDate;
                                const dealId = dataParamReq.current[idx].dealId
                                const error = modalError && Array.isArray(modalError) && modalError.find(error => error.dealId === dealId)

                                const {
                                    minDateDefault,
                                    maxDateDefault,
                                    modalExtensionError
                                } = prolongCalculator[idx].calculateDateDefault({
                                    idx,
                                    extensionDate,
                                    newEndDate,
                                    modalError,
                                    baseAmount,
                                    fieldPosition
                                })
                                const isNewGlobalProlong = prolongCalculator[idx].isNewGlobalProlong
                                const isPenaltiesAmount = !!fieldPosition.penalties?.length
                                const isSumFunctionDisabled = !Number(totalSum)

                                const getDatePickerValue = () => {
                                    if (error?.errorCode || fieldPosition?.message) {
                                        return extensionDate;
                                    }
                                    return isNewGlobalProlong ? (dateFromSum || extensionDate) : extensionDate;
                                };
                                const totalPenalty = fieldPosition.penalties?.filter(item => selectedPenalties[dealId]?.includes(item.number))
                                    .reduce((sum, item) => sum + item.penalty, 0);

                                const sum = new Big(baseAmount)
                                    .plus(isNewGlobalProlong ? 0 : new Big(delayAmount || 0))
                                    .plus(extensionAmount)
                                    .plus(parseFloat(totalPenalty || 0))
                                    .round(2, Big.roundHalfUp)
                                    .toFixed(2);
                                const sumPercent = new Big(baseAmount)
                                    .plus(delayAmount)
                                    .plus(totalPenaltyAmount)
                                    .round(2, Big.roundHalfUp)
                                    .toFixed(2);

                                const shouldShowExtension = notEmptyExtensionAmount &&
                                    ((!isNewGlobalProlong && fieldPosition.extensionAmount !== 0) ||
                                        (Boolean(fieldPosition.calculatedExtensionDays) && !modalExtensionError[idx]?.error));

                                return (<div className="prolong-item" key={fieldPosition?.dealId}>
                                    {showInfoMessage(fieldPosition)}

                                    {renderProlongErrorMessage(error, idx)}

                                    {fieldPosition?.message && (<Message
                                        type="error"
                                        text={translateErrorMessage(fieldPosition?.message)}/>)}

                                    {isNewGlobalProlong && !error?.errorCode && modalExtensionError[idx]?.notification &&
                                        modalExtensionError[idx].notification.map((error, idx) => (
                                            <Message key={idx} type={error.type} text={error.message}/>
                                        ))}
                                    {notEmptyBaseAmount && fieldPosition.baseAmount !== 0 && (
                                        <Collapse collapsible="header">
                                            <Panel
                                                showArrow={true}
                                                header={<p className="prolong-datacollapse-header-content">
                                                    {headerContent(idx)}
                                                    <b>№{fieldPosition.dealNumber || fieldPosition.dealId}</b> за{' '}
                                                    {returnEndStr(fieldPosition?.baseDays)} по{' '}
                                                    {isNewGlobalProlong ? firstBasePercent.toFixed(2) : basePercent.toFixed(2)}%
                                                </p>}
                                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                                className="block-datacollapse-container prolong-datacollapse-container"
                                                extra={<div className="prolong-extra-end">
                                                    <p>
                                                        <b className="success">
                                                            {fieldPosition?.baseAmount.toFixed(2)}
                                                        </b>{' '}
                                                        руб.
                                                    </p>
                                                </div>}>
                                            </Panel>
                                        </Collapse>)}
                                    {notEmptyDelayAmount && fieldPosition.delayAmount !== 0 && !isNewGlobalProlong && (
                                        <Collapse collapsible="header">
                                            <Panel
                                                showArrow={true}
                                                header={<p className="prolong-datacollapse-header-content">
                                                    {accessFeatures['cashboxNameTypeOne']
                                                        ? basisForCloseAndProlongDeal.CREDITON.DELAY_EXPIRE
                                                        : basisForCloseAndProlongDeal.VISEM.DELAY_EXPIRE
                                                    }
                                                    <b>№{fieldPosition.dealNumber || fieldPosition.dealId}</b> за{' '}
                                                    {returnEndStr(fieldPosition?.delayDays)} по{' '}
                                                    {fieldPosition?.delayPercent.toFixed(2)}%
                                                </p>}
                                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                                className="block-datacollapse-container prolong-datacollapse-container"
                                                extra={<div className="prolong-extra-end">
                                                    <p>
                                                        <b className="success">
                                                            {fieldPosition?.delayAmount.toFixed(2)}
                                                        </b>{' '}
                                                        руб.
                                                    </p>
                                                </div>}>
                                                <table className="table prolong-table">
                                                    <tbody>
                                                    <tr>
                                                        <td>
                                                            <p>1</p>
                                                        </td>
                                                        <td>
                                                            <p>Проценты за пользование микрозаймом</p>
                                                        </td>
                                                        <td className="prolong-table-right">
                                                            <p>{fieldPosition?.delayAmount.toFixed(2)} руб.</p>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </Panel>
                                        </Collapse>)}
                                    {isNewGlobalProlong && notEmptyExtensionAmount && fieldPosition.extensionAmount !== 0 && (
                                        <Collapse collapsible="header">
                                            <Panel
                                                showArrow={true}
                                                header={<p className="prolong-datacollapse-header-content">
                                                    {isExtensionThreeImplementaition ? basisForCloseAndProlongDeal.CREDITON.BASE_EXPIRE : basisForCloseAndProlongDeal.VISEM.NEW_DELAY_EXPIRE}
                                                    <b>№{fieldPosition.dealNumber || fieldPosition.dealId}</b> за{' '}
                                                    {returnEndStr(fieldPosition?.extensionDays)}
                                                    {` по ${basePercent.toFixed(2)}%`}
                                                </p>}
                                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                                className="block-datacollapse-container prolong-datacollapse-container"
                                                extra={<div className="prolong-extra-end">
                                                    <p>
                                                        <b className="success">
                                                            {fieldPosition?.extensionAmount.toFixed(2)}
                                                        </b>{' '}руб.
                                                    </p>
                                                </div>}>
                                            </Panel>
                                        </Collapse>)}
                                    {shouldShowExtension && (
                                        <Collapse collapsible="header">
                                            <Panel
                                                showArrow={true}
                                                header={<p className="prolong-datacollapse-header-content">
                                                    Продление микрозаймов{' '}
                                                    <b>№{fieldPosition.dealNumber || fieldPosition.dealId}</b> на{' '}
                                                    {returnEndStr(isNewGlobalProlong ? fieldPosition?.calculatedExtensionDays : fieldPosition?.extensionDays)} из{' '}
                                                    {returnEndStr(fieldPosition?.extensionMaxDays)}
                                                </p>}
                                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                                className="block-datacollapse-container prolong-datacollapse-container"
                                                extra={<div className="prolong-extra-end"><p>
                                                    <b className="success">
                                                        {isNewGlobalProlong ? '0' : fieldPosition?.extensionAmount.toFixed(2)}
                                                    </b>{' '} руб.
                                                </p>
                                                </div>}>

                                                {!isNewGlobalProlong && <table className="table prolong-table">
                                                    <tbody>
                                                    <tr>
                                                        <td>
                                                            <p>1</p>
                                                        </td>
                                                        <td>
                                                            <p>НДС</p>
                                                        </td>
                                                        <td className="prolong-table-right">
                                                            <p>{fieldPosition?.extensionVat.toFixed(2)} руб.</p>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <p>2</p>
                                                        </td>
                                                        <td>
                                                            <p>За пользование микрозаймом</p>
                                                        </td>
                                                        <td className="prolong-table-right">
                                                            <p>{fieldPosition?.extensionAmount.toFixed(2)} руб.</p>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>}
                                            </Panel>
                                        </Collapse>)}
                                    {isPenaltiesAmount &&
                                        <PenaltySelector
                                            penalties={fieldPosition.penalties}
                                            dealId={fieldPosition.dealId}
                                            index={idx}
                                            selectedPenalties={selectedPenalties}
                                            setSelectedPenalties={setSelectedPenalties}
                                            setTotalPenaltyAmount={setTotalPenaltyAmount}
                                            extensionDate={dateFromSum || extensionDate}
                                            onPenaltyChange={handlePenaltyAmount}
                                            isCalculated={isCalculated}
                                        />
                                    }
                                    <Collapse collapsible="header">
                                        <Panel
                                            showArrow={true}
                                            header={<p>
                                                <b>
                                      <span>
                                        №{fieldPosition.dealNumber || fieldPosition.dealId}
                                      </span>
                                                </b>{' '}
                                                {checkPaymentOrExtension(dateFromSum || extensionDate, idx)} до
                                            </p>}
                                            headerClass="block-datacollapse-header prolong-datacollapse-header"
                                            className="block-datacollapse-container prolong-datacollapse-container"
                                            extra={<div className="prolong-extra-space">
                                                <HDatePicker
                                                    name={`deals[${idx}].extensionDate`}
                                                    className="input prolong-input"
                                                    min={minDateDefault}
                                                    max={maxDateDefault}
                                                    defaultValue={getDatePickerValue()}
                                                    onInput={(event) => debounceHandleChangeItem(event, idx, i.dealId)}
                                                />
                                                {
                                                    <p className={`font-size-larger ${isNonCashOperationCrediton ? 'font-color-red' : ''}`}>
                                                        <b>{sum} руб.</b>
                                                    </p>}
                                            </div>}>
                                            {!isNewGlobalProlong && (fieldPosition?.extensionAmount || fieldPosition?.baseAmount || fieldPosition?.delayAmount) && (
                                                <table className="table prolong-table">
                                                    <tbody>
                                                    {fieldPosition?.extensionAmount && (<tr>
                                                        <td>
                                                            <p>1</p>
                                                        </td>
                                                        <td>
                                                            <p>За пользование микрозаймом</p>
                                                        </td>
                                                        <td className="prolong-table-right">
                                                            <p>{fieldPosition?.extensionAmount.toFixed(2)} руб.</p>
                                                        </td>
                                                    </tr>)}
                                                    {(fieldPosition?.basePercent || fieldPosition?.delayPercent) && (
                                                        <tr>
                                                            <td>
                                                                <p>{fieldPosition?.extensionAmount ? 2 : 1}</p>
                                                            </td>
                                                            <td>
                                                                <p>{isNewGlobalProlong ? basisForCloseAndProlongDeal.VISEM.NEW_DELAY_EXPIRE : 'Проценты за неустойку'}</p>
                                                            </td>
                                                            <td className="prolong-table-right">
                                                                <p>{sumPercent} руб.</p>
                                                            </td>
                                                        </tr>)}
                                                    </tbody>
                                                </table>)}
                                        </Panel>
                                    </Collapse>
                                    {isExtensionThreeImplementaition && isNewGlobalProlong &&
                                        <Collapse collapsible="header">
                                            <Panel
                                                showArrow={true}
                                                header={<p><b>
                                                                <span>
                                                                    №{fieldPosition.dealNumber || fieldPosition.dealId}
                                                                </span></b>{' '}новая дата договора</p>}
                                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                                className="block-datacollapse-container prolong-datacollapse-container"
                                                extra={<div className="prolong-extra-space flex-end">
                                                    <div className="date-box">
                                                        {moment(fieldPosition.newEndDate).format('DD.MM.YYYY')}
                                                    </div>
                                                </div>}>
                                            </Panel>
                                        </Collapse>}
                                    {(isExtensionThreeImplementaition || !isNewGlobalProlong) &&
                                        <div className="form-row m-none display-inline">
                                            <div className="form-row-item p-l-spacer-3-5">
                                                <div
                                                    className="form-element flex-direction-row flex-space-between">
                                                    <label
                                                        className="form-element-label m-vertical-auto"
                                                        htmlFor="amount">
                                                        Расчет от суммы:
                                                    </label>
                                                    <div className="form-element-data number">
                                                        <HInput
                                                            h="true"
                                                            type="text"
                                                            name={`deals[${idx}].sum`}
                                                            className="input"
                                                            defaultValue={dataParamReq.current[idx].sum}
                                                            decimals={2}
                                                            onChange={(event) => debounceHandleChangeItem(event.target, idx, i.dealId)}
                                                            disabled={isSumFunctionDisabled}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>}
                                    <HInput
                                        type="hidden"
                                        name={`deals[${idx}].dealId`}
                                        defaultValue={fieldPosition.dealId}
                                    />
                                </div>);
                            })}
                        </>)}
                    </>}
                    {!isNonCashOperationCrediton && notEmpty && prolongCalculate.find((i) => i?.extensionAmount || i?.baseAmount || i?.delayAmount,) && (
                        <Collapse collapsible="header">
                            <Panel
                                showArrow={true}
                                header={<p
                                    className="prolong-datacollapse-header-content font-size-larger font-color-red">
                                    <b>Взять с клиента</b>
                                </p>}
                                headerClass="block-datacollapse-header prolong-datacollapse-header"
                                className="block-datacollapse-container"
                                extra={<div className="prolong-extra-end">
                                    <p className="font-size-larger font-color-red">
                                        <b>{totalSum} руб.</b>
                                    </p>
                                </div>}>
                                {!isNewGlobalProlong && <table className="table prolong-table">
                                    <tbody>
                                    {notEmpty && (<tr>
                                        <td>
                                            <p>1</p>
                                        </td>
                                        <td>
                                            <p>За пользование микрозаймом</p>
                                        </td>
                                        <td className="prolong-table-right">
                                            <p>{totalSumExtensionAmount} руб.</p>
                                        </td>
                                    </tr>)}
                                    {notEmpty && prolongCalculate.find((i) => i?.baseAmount || i?.delayAmount) && (
                                        <tr>
                                            <td>
                                                <p>{prolongCalculate.find((i) => i?.extensionAmount) ? 2 : 1}</p>
                                            </td>
                                            <td>
                                                <p>Проценты за неустойку</p>
                                            </td>
                                            <td className="prolong-table-right">
                                                <p>{totalSumPercent} руб.</p>
                                            </td>
                                        </tr>)}
                                    </tbody>
                                </table>}
                            </Panel>
                        </Collapse>)}
                    <div className="prolong-calculate">
                        {totalSum !== 0 && isNonCashOperationCrediton
                            ? <PaymentTypeSelector
                                form={form}
                                maxTotalSum={totalSumWithPenalties ?? totalSum}
                                calculateTotalSum={total}
                                inputFieldName={'deposit'}
                                setCashChangePayment={setChange}
                                setPaymentInputs={setPaymentInputs}/>
                            : <NonCash onFocus={showDelivery} onChange={handleInputChange}/>
                        }
                        {!isNonCashOperationCrediton && <div className='prolong-amountСontributed'>
                            <h3>Итого внесено:</h3>
                            <h3>{total.toFixed(2) || 0}</h3>
                        </div>}
                    </div>
                </div>
                <div className="modal-footer clientContract-modal-footer">
                    {permissionsAccess('accept.money') && <ActionButton text="Принять деньги"
                                                                        styleName={disableButton ? "button-light" : "button"}
                                                                        disabled={disableButton || modalError} submit
                    />}
                    <ActionButton text="Отменить" func={handleLocalOpenModal} styleName="button-light"/>
                </div>
                <BasicAlert onClose={() => setBasicAlertModal(false)}
                            message={alertMessage}
                            isOpen={basicAlertModal}
                            isError={true}
                />
                <ConfirmModal
                    isOpen={state.isModal}
                    handleClose={isProlongDeal ? onCloseModal : handleLocalCloseModal}
                    handleAction={isProlongDeal ? onProlongContract : goBack}
                    headerText={isProlongDeal ? 'Продлить договор?' : 'Отменить продление договора?'}
                    bodyText={isProlongDeal ? '' : 'Внесённые данные не сохранятся.'}
                />
            </form>
        </FormProvider>
    </>);
};

export default ProlongModalLayout;
